// 실제 생성엔진 출력(RA/JSA/TBM 전체) 정적 로더 — public/assets/ac-examples/examples.json
// ── RA (위험성평가) ──────────────────────────────────────────────────────────
export interface RaMeasure { tier: string; measure: string }
export interface RaLegal { article_no: string; article_title: string; penalty_level: string }
export interface RaHazard {
  unit_task: string; hazard_category: string; hazard: string; cause: string
  current_controls: string; frequency: number; severity: number
  measures: RaMeasure[]; residual_frequency: number; residual_severity: number
  case_ref?: string; legal_basis?: RaLegal
}
export interface RaCase { case_id: string; similarity: number; summary: string; hazard: string; prevention: string }
export interface RaArticle { article_no: string; article_title: string; penalty_level: string; must_check: boolean; relevance?: number }
export interface RaCompliance { criminal_count: number; matched_count: number; must_check_count: number; grade?: string | null; articles: RaArticle[] }
export interface Ra { cases: RaCase[]; hazards: RaHazard[]; compliance?: RaCompliance | null; unit_tasks?: string[] }

// ── JSA ──────────────────────────────────────────────────────────────────────
export interface JsaRow {
  step: string; hazard: string; safeguards: string
  frequency_before: number; severity_before: number; risk_level_before: string; risk_level_value: number
  measures: string; frequency_after: number | null; severity_after: number | null; risk_level_after: string
  pic: string
}
export interface PpeItem { name: string; cert: string; grade: string; reasons: string[] }
export interface JsaHeader { work_name: string; work_department: string; work_date: string; supervisor: string; workers: string }
export interface Jsa { header: JsaHeader; rows: JsaRow[]; recommended_ppe?: { items: PpeItem[]; basis: string } }

// ── TBM ──────────────────────────────────────────────────────────────────────
export interface TbmHazard { no: number; hazard: string; description: string; risk_level: string; safety_measures: string[] }
export interface TbmScenario { scenario_number: number; title: string; story: string; prevention_points: string[] }
export interface Tbm {
  briefing?: {
    work_summary?: string; hazards?: TbmHazard[]; safety_rules?: string[]
    legal_basis?: string[]; ppe_required?: string[]; emergency_actions?: string[]
    ppe_recommended?: { items?: PpeItem[] }
  }
  scenarios?: TbmScenario[]
  poster_static?: string; comic_static?: string
  accidents?: { summary: string; accident_type?: string; cause?: string }[]
  laws?: { law_name: string; doc_type?: string; text_preview?: string }[]
}

export interface ExampleWork {
  id: string
  label: { ko: string; en: string }
  keywords: string[]
  ra: Ra
  jsa: Jsa
  tbm?: Tbm
}

// 언어별 캐시 — ko=examples.json, en=examples.en.json(번역본). en 없으면 ko로 폴백.
const _cache: Record<string, ExampleWork[]> = {}
export async function loadExamples(lang: 'ko' | 'en' = 'ko'): Promise<ExampleWork[]> {
  if (_cache[lang]) return _cache[lang]
  const file = lang === 'en' ? 'examples.en.json' : 'examples.json'
  const res = await fetch(`/assets/ac-examples/${file}`)
  if (!res.ok) {
    if (lang === 'en') return loadExamples('ko')   // 영어 파일 미배포 시 안전 폴백
    throw new Error('examples load failed')
  }
  const data = await res.json()
  _cache[lang] = (data.works || []) as ExampleWork[]
  return _cache[lang]
}

export function matchWork(works: ExampleWork[], query: string): ExampleWork | null {
  if (!works.length) return null
  const q = (query || '').toLowerCase()
  let best = works[0], bestScore = -1
  for (const w of works) {
    const score = w.keywords.reduce((s, k) => s + (k && q.includes(k.toLowerCase()) ? 1 : 0), 0)
    if (score > bestScore) { best = w; bestScore = score }
  }
  return best
}

// 위험도 밴드 (빈도×강도 또는 risk_level_value)
export function riskBand(score: number): { ko: string; en: string; cls: string } {
  if (score >= 15) return { ko: '매우높음', en: 'Critical', cls: 'bg-red-500/15 text-red-400 border-red-500/30' }
  if (score >= 9) return { ko: '높음', en: 'High', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' }
  if (score >= 4) return { ko: '보통', en: 'Medium', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' }
  return { ko: '낮음', en: 'Low', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' }
}

export const TIER_LABEL: Record<string, { ko: string; en: string }> = {
  elimination: { ko: '제거', en: 'Elimination' },
  substitution: { ko: '대체', en: 'Substitution' },
  engineering: { ko: '공학적', en: 'Engineering' },
  administrative: { ko: '관리적', en: 'Administrative' },
  ppe: { ko: '보호구', en: 'PPE' },
}
export const TIER_CLS: Record<string, string> = {
  elimination: 'bg-red-500/12 text-red-300',
  substitution: 'bg-orange-500/12 text-orange-300',
  engineering: 'bg-blue-500/12 text-blue-300',
  administrative: 'bg-violet-500/12 text-violet-300',
  ppe: 'bg-emerald-500/12 text-emerald-300',
}

// "[공학적] ... \n[관리적] ..." → [{tag, text}]
export function parseTagged(s: string): { tag: string; text: string }[] {
  if (!s) return []
  const out: { tag: string; text: string }[] = []
  for (const line of s.split('\n')) {
    const m = line.match(/^\s*\[([^\]]+)\]\s*(.+)$/)
    if (m) out.push({ tag: m[1].trim(), text: m[2].trim() })
    else if (line.trim()) out.push({ tag: '', text: line.trim() })
  }
  return out
}
